import { useDocumentTitle } from "@/lib/use-document-title";
import { useMemo, useRef, useState } from "react";
import { StaffLayout } from "@/components/StaffLayout";
import { useEvents, useEventMutations, useStores, useEventRegistrations, useMembers, type EventRow } from "@/lib/data-hooks";
import { Calendar, Plus, Pencil, Trash2, MapPin, Clock, X, CheckCircle2, Users, Upload, Loader2, UserCheck, Search, Copy, History, Send, AlertTriangle } from "lucide-react";
import { useConfirm } from "@/components/ConfirmDialog";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/lib/auth-context";
import { useQuery, useQueryClient, useMutation } from "@tanstack/react-query";


function emptyEvent(storeId: string | null): Partial<EventRow> {
  const d = new Date(); d.setDate(d.getDate() + 7); d.setHours(14, 0, 0, 0);
  return { title: "", store_id: storeId, starts_at: d.toISOString(), max_attendees: 20, description: "", active: true };
}

function EventAdminPage() {
  useDocumentTitle("Event — ÅF-Portal");
  const { data: events = [], isLoading } = useEvents();
  const { data: stores = [] } = useStores();
  const { create, update, remove } = useEventMutations();
  const confirm = useConfirm();
  const storeMap = new Map(stores.map((s) => [s.id, s.name]));


  const [q, setQ] = useState("");
  const [storeF, setStoreF] = useState("");
  const [showPast, setShowPast] = useState(false);
  const [editing, setEditing] = useState<Partial<EventRow> | null>(null);
  const [attendeesOf, setAttendeesOf] = useState<EventRow | null>(null);
  const [checkinOf, setCheckinOf] = useState<EventRow | null>(null);
  const [campaignOf, setCampaignOf] = useState<EventRow | null>(null);

  const [toast, setToast] = useState<string | null>(null);
  const show = (m: string) => { setToast(m); setTimeout(() => setToast(null), 2200); };

  const now = new Date().toISOString();
  const filtered = useMemo(() => {
    return events
      .filter((e) => (showPast ? true : e.starts_at >= now))
      .filter((e) => (storeF ? storeMap.get(e.store_id ?? "") === storeF : true))
      .filter((e) => (q ? e.title.toLowerCase().includes(q.toLowerCase()) : true));
  }, [events, showPast, storeF, q, now, storeMap]);

  const save = async (ev: Partial<EventRow>) => {
    if (!ev.title?.trim()) return;
    if (ev.id) { await update.mutateAsync(ev as any); show("Eventet uppdaterades"); }
    else { await create.mutateAsync(ev); show("Nytt event skapades"); }
    setEditing(null);
  };
  const duplicate = async (e: EventRow) => {
    const d = new Date(); d.setDate(d.getDate() + 7);
    const src = new Date(e.starts_at);
    d.setHours(src.getHours(), src.getMinutes(), 0, 0);
    let ends: string | null = null;
    if (e.ends_at) {
      const dur = new Date(e.ends_at).getTime() - src.getTime();
      ends = new Date(d.getTime() + dur).toISOString();
    }
    const created = await create.mutateAsync({
      title: e.title,
      description: e.description,
      store_id: e.store_id,
      cover_image_url: e.cover_image_url,
      max_attendees: e.max_attendees,
      starts_at: d.toISOString(),
      ends_at: ends,
      active: false,
    } as Partial<EventRow>);
    show("Utkast skapat – justera och spara för att publicera");
    setEditing(created);
  };

  const del = async (e: EventRow) => {
    if (!(await confirm({ title: `Ta bort "${e.title}"?`, destructive: true, confirmText: "Ta bort" }))) return;
    await remove.mutateAsync(e.id); show("Eventet togs bort");
  };

  const upcoming = events.filter((e) => e.starts_at >= now);

  return (
    <StaffLayout title="Event">
      <p className="text-muted-foreground mb-6 max-w-2xl">Skapa, redigera och följ upp event på dina butiker. Medlemmar ser dem direkt i Access-appen.</p>

      <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
        <Kpi icon={Calendar} label="Kommande event" value={upcoming.length.toString()} />
        <Kpi icon={MapPin} label="Butiker med event" value={new Set(upcoming.map(e => e.store_id)).size.toString()} />
        <Kpi icon={Clock} label="Totalt platser" value={upcoming.reduce((s, e) => s + e.max_attendees, 0).toString()} />
      </div>

      <div className="card-soft p-4 mb-5 flex flex-wrap gap-3 items-center">
        <div className="relative flex-1 min-w-[200px]">
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Sök event..." className="w-full bg-white border border-border rounded-lg px-3 py-2 text-sm" />
        </div>
        <select value={storeF} onChange={(e) => setStoreF(e.target.value)} className="bg-white border border-border rounded-lg px-3 py-2 text-sm">
          <option value="">Alla butiker</option>
          {stores.map((s) => <option key={s.id}>{s.name}</option>)}
        </select>
        <button
          type="button"
          onClick={() => setShowPast((v) => !v)}
          aria-pressed={showPast}
          className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-display font-semibold border transition ${showPast ? "bg-primary text-white border-primary" : "border-border text-muted-foreground hover:bg-muted"}`}
        >
          <History size={15} /> {showPast ? "Dölj tidigare event" : "Visa tidigare event"}
        </button>
        <button onClick={() => setEditing(emptyEvent(stores[0]?.id ?? null))} className="ml-auto inline-flex items-center gap-2 bg-[var(--color-primary)] text-white px-4 py-2 rounded-full text-sm font-display font-semibold hover:opacity-90 transition">
          <Plus size={16} /> Nytt event
        </button>
      </div>

      {isLoading && <p className="text-sm text-muted-foreground">Laddar…</p>}

      <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4">
        {filtered.map((e) => {
          const past = e.starts_at < now;
          return (
            <div key={e.id} className={`card-soft p-5 ${past ? "opacity-60" : ""}`}>
              <div className="flex items-start justify-between gap-3 mb-3">
                <div className="min-w-0">
                  <div className="font-display font-semibold text-base leading-tight">{e.title}</div>
                  <div className="text-xs text-muted-foreground mt-1 flex items-center gap-1.5"><MapPin size={12} /> {e.store_id ? storeMap.get(e.store_id) : "—"}</div>
                </div>
                <span className={`shrink-0 text-[10px] uppercase tracking-wider px-2 py-1 rounded-full font-display font-semibold ${past ? "bg-zinc-200 text-zinc-600" : e.active ? "bg-emerald-500/15 text-emerald-700" : "bg-amber-500/15 text-amber-700"}`}>
                  {past ? "Genomfört" : e.active ? "Aktivt" : "Dolt"}
                </span>
              </div>

              <div className="text-sm text-muted-foreground mb-3 flex items-center gap-1.5">
                <Clock size={13} /> {new Date(e.starts_at).toLocaleString("sv-SE", { day: "2-digit", month: "long", year: "numeric", hour: "2-digit", minute: "2-digit" })}
              </div>

              <div className="text-xs text-muted-foreground">Max {e.max_attendees} platser</div>
              {e.description && <p className="text-sm text-foreground mt-2 line-clamp-3">{e.description}</p>}

              <div className="flex flex-wrap gap-2 mt-4">
                <button onClick={() => setAttendeesOf(e)} className="flex-1 min-w-[120px] inline-flex items-center justify-center gap-1.5 py-2 rounded-full bg-primary text-white text-xs font-display font-semibold hover:opacity-90 transition">
                  <Users size={12} /> Visa anmälda
                </button>
                <button onClick={() => setCheckinOf(e)} className="flex-1 min-w-[120px] inline-flex items-center justify-center gap-1.5 py-2 rounded-full border border-primary text-primary text-xs font-display font-semibold hover:bg-primary/10 transition">
                  <UserCheck size={12} /> Närvaro
                </button>
                <button onClick={() => setCampaignOf(e)} className="inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-full border border-border text-xs font-display hover:bg-muted transition" aria-label="Utskick" title="Utskick">
                  <Send size={12} />
                </button>
                <button onClick={() => duplicate(e)} disabled={create.isPending} className="inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-full border border-border text-xs font-display hover:bg-muted transition disabled:opacity-50" aria-label="Duplicera event" title="Duplicera event">
                  <Copy size={12} />
                </button>
                <button onClick={() => setEditing(e)} className="inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-full border border-border text-xs font-display hover:bg-muted transition" aria-label="Redigera">
                  <Pencil size={12} />
                </button>
                <button onClick={() => del(e)} className="inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-full border border-border text-rose-600 hover:bg-rose-50 transition" aria-label="Ta bort">
                  <Trash2 size={12} />
                </button>
              </div>

            </div>
          );
        })}
        {!filtered.length && !isLoading && (
          <div className="col-span-full text-center py-12 text-muted-foreground text-sm">Inga event matchar dina filter.</div>
        )}
      </div>

      {editing && <EventDialog initial={editing} stores={stores} onClose={() => setEditing(null)} onSave={save} />}
      {attendeesOf && <AttendeesDialog event={attendeesOf} storeName={attendeesOf.store_id ? storeMap.get(attendeesOf.store_id) ?? null : null} onClose={() => setAttendeesOf(null)} />}
      {checkinOf && <CheckinDialog event={checkinOf} onClose={() => setCheckinOf(null)} />}
      {campaignOf && <CampaignDialog event={campaignOf} storeName={campaignOf.store_id ? storeMap.get(campaignOf.store_id) ?? "—" : "—"} onClose={() => setCampaignOf(null)} onSent={(n) => { setCampaignOf(null); show(`Utskick köat till ${n} mottagare`); }} />}


      {toast && (
        <div className="fixed bottom-6 right-6 bg-[var(--color-ink)] text-white px-4 py-3 rounded-lg shadow-lg flex items-center gap-2 text-sm z-50">
          <CheckCircle2 size={16} className="text-emerald-400" /> {toast}
        </div>
      )}
    </StaffLayout>
  );
}

function Kpi({ icon: Icon, label, value }: { icon: any; label: string; value: string }) {
  return (
    <div className="card-soft p-5">
      <Icon className="mb-2 text-[var(--color-primary)]" size={20} />
      <div className="text-xs text-muted-foreground uppercase tracking-wider">{label}</div>
      <div className="font-display font-bold text-2xl mt-1">{value}</div>
    </div>
  );
}

// Konvertera ISO (UTC) -> "YYYY-MM-DDTHH:mm" i LOKAL tid för <input type="datetime-local">
function toLocalInputValue(iso: string | null | undefined): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "";
  const pad = (n: number) => n.toString().padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function EventDialog({ initial, stores, onClose, onSave }: { initial: Partial<EventRow>; stores: any[]; onClose: () => void; onSave: (e: Partial<EventRow>) => void }) {
  const [e, setE] = useState<Partial<EventRow>>(initial);
  const dateValue = toLocalInputValue(e.starts_at);
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60">
      <div className="bg-background rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-6 py-4 border-b border-border">
          <h3 className="font-display font-semibold text-lg">{initial.id ? "Redigera event" : "Nytt event"}</h3>
          <button onClick={onClose} aria-label="Stäng" className="p-1.5 rounded-lg hover:bg-muted"><X size={18} /></button>
        </div>
        <form onSubmit={(ev) => { ev.preventDefault(); onSave(e); }} className="p-6 space-y-4">
          <Field label="Titel">
            <input value={e.title ?? ""} onChange={(x) => setE({ ...e, title: x.target.value })} required className="input-mini" />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Butik">
              <select value={e.store_id ?? ""} onChange={(x) => setE({ ...e, store_id: x.target.value || null })} className="input-mini">
                <option value="">— Ingen —</option>
                {stores.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
            </Field>
            <Field label="Datum & tid">
              <input
                type="datetime-local"
                value={dateValue}
                onChange={(x) => {
                  // x.target.value är "YYYY-MM-DDTHH:mm" i lokal tid. new Date(...) tolkar det som lokal tid → korrekt UTC vid toISOString().
                  const v = x.target.value;
                  setE({ ...e, starts_at: v ? new Date(v).toISOString() : "" });
                }}
                required
                className="input-mini"
              />
            </Field>
            <Field label="Antal platser">
              <input type="number" min={1} value={e.max_attendees ?? 20} onChange={(x) => setE({ ...e, max_attendees: Number(x.target.value) })} className="input-mini" />
            </Field>
          </div>
          <Field label="Bild (cover)">
            <ImageUploadField
              value={e.cover_image_url ?? ""}
              onChange={(url) => setE({ ...e, cover_image_url: url || null })}
            />
          </Field>
          <Field label="Beskrivning">
            <textarea value={e.description ?? ""} onChange={(x) => setE({ ...e, description: x.target.value })} rows={3} className="input-mini" />
          </Field>
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={e.active ?? true} onChange={(x) => setE({ ...e, active: x.target.checked })} /> Synligt för medlemmar
          </label>
          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className="px-4 py-2 rounded-full border border-border text-sm font-display hover:bg-muted">Avbryt</button>
            <button type="submit" className="px-4 py-2 rounded-full bg-[var(--color-primary)] text-white text-sm font-display font-semibold hover:opacity-90">{initial.id ? "Spara" : "Skapa event"}</button>
          </div>
        </form>
      </div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="block text-xs uppercase tracking-wider text-muted-foreground mb-1.5 font-display">{label}</span>
      {children}
    </label>
  );
}

function ImageUploadField({ value, onChange }: { value: string; onChange: (url: string) => void }) {
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  const handleFile = async (file: File) => {
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { setError("Max 5 MB."); return; }
    if (!file.type.startsWith("image/")) { setError("Endast bildfiler."); return; }
    setError(null); setUploading(true);
    try {
      const ext = file.name.split(".").pop() || "jpg";
      const path = `${crypto.randomUUID()}.${ext}`;
      const { error: upErr } = await supabase.storage.from("event-images").upload(path, file, { cacheControl: "3600", upsert: false, contentType: file.type });
      if (upErr) throw upErr;
      const { data } = supabase.storage.from("event-images").getPublicUrl(path);
      onChange(data.publicUrl);
    } catch (err: any) {
      setError(err.message || "Uppladdning misslyckades");
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="space-y-2">
      <div className="flex gap-2">
        <input
          type="url"
          placeholder="https://… eller ladda upp"
          value={value}
          onChange={(x) => onChange(x.target.value)}
          className="input-mini flex-1"
        />
        <button
          type="button"
          onClick={() => fileRef.current?.click()}
          disabled={uploading}
          className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md border border-border text-xs font-display font-semibold hover:bg-muted transition disabled:opacity-50"
        >
          {uploading ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}
          {uploading ? "Laddar…" : "Ladda upp"}
        </button>
        <input
          ref={fileRef}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={(x) => { const f = x.target.files?.[0]; if (f) handleFile(f); x.target.value = ""; }}
        />
      </div>
      {error && <p className="text-xs text-rose-600">{error}</p>}
      {value && (
        <div className="rounded-lg overflow-hidden border border-border">
          <img src={value} alt="Förhandsvisning" className="w-full h-32 object-cover" onError={(ev) => { (ev.currentTarget as HTMLImageElement).style.display = "none"; }} />
        </div>
      )}
    </div>
  );
}

function AttendeesDialog({ event, storeName, onClose }: { event: EventRow; storeName: string | null; onClose: () => void }) {
  const { data: regs = [], isLoading } = useEventRegistrations(event.id);
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60" onClick={onClose}>
      <div className="bg-background rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between px-6 py-4 border-b border-border sticky top-0 bg-background">
          <div>
            <h3 className="font-display font-semibold text-lg">{event.title}</h3>
            <div className="text-xs text-muted-foreground mt-0.5">
              {new Date(event.starts_at).toLocaleString("sv-SE", { day: "2-digit", month: "long", year: "numeric", hour: "2-digit", minute: "2-digit" })}
              {storeName && ` · ${storeName}`} · {regs.length}/{event.max_attendees} anmälda
            </div>
          </div>
          <button onClick={onClose} aria-label="Stäng" className="p-1.5 rounded-lg hover:bg-muted"><X size={18} /></button>
        </div>
        <div className="p-6">
          {isLoading && <p className="text-sm text-muted-foreground">Laddar…</p>}
          {!isLoading && !regs.length && (
            <p className="text-sm text-muted-foreground text-center py-8">Inga anmälningar ännu.</p>
          )}
          {!!regs.length && (
            <div className="card-soft overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-muted">
                  <tr className="text-left text-xs uppercase tracking-wider text-muted-foreground">
                    <th className="px-3 py-2">Medlem</th>
                    <th className="px-3 py-2">Kontakt</th>
                    <th className="px-3 py-2">Status</th>
                    <th className="px-3 py-2">Anmäld</th>
                  </tr>
                </thead>
                <tbody>
                  {regs.map((r: any) => (
                    <tr key={r.id} className="border-t border-border">
                      <td className="px-3 py-2">
                        <div className="font-display font-semibold">{r.member?.first_name} {r.member?.last_name}</div>
                        <div className="text-xs text-muted-foreground font-mono">{r.member?.member_number}</div>
                      </td>
                      <td className="px-3 py-2 text-xs">
                        <div>{r.member?.email}</div>
                        <div className="text-muted-foreground">{r.member?.phone ?? "—"}</div>
                      </td>
                      <td className="px-3 py-2">
                        {r.checked_in_at ? (
                          <span className="inline-flex items-center gap-1 text-xs text-emerald-700 bg-emerald-500/10 px-2 py-0.5 rounded-full font-display font-semibold"><CheckCircle2 size={11} /> Incheckad</span>
                        ) : (
                          <span className="inline-flex text-xs px-2 py-0.5 rounded-full bg-primary/10 text-primary font-display font-semibold capitalize">{r.status}</span>
                        )}
                      </td>
                      <td className="px-3 py-2 text-xs text-muted-foreground">
                        {new Date(r.created_at).toLocaleDateString("sv-SE")}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default EventAdminPage;

// ============================================================
// NÄRVARO — pricka av medlemmar på plats (även drop-in)
// ============================================================
type CheckinRow = { id: string; member_id: string; checked_in_at: string | null; status: string };

function CheckinDialog({ event, onClose }: { event: EventRow; onClose: () => void }) {
  const { user } = useAuth();
  const qc = useQueryClient();
  const [q, setQ] = useState("");
  const [onlyCheckedIn, setOnlyCheckedIn] = useState(false);
  const { data: members = [], isLoading: loadingMembers } = useMembers();

  const key = ["event_checkins", event.id];
  const { data: regs = [], isLoading: loadingRegs } = useQuery({
    queryKey: key,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("event_registrations")
        .select("id, member_id, checked_in_at, status")
        .eq("event_id", event.id);
      if (error) throw error;
      return (data ?? []) as CheckinRow[];
    },
  });

  const regByMember = useMemo(() => new Map(regs.map((r) => [r.member_id, r])), [regs]);
  const checkedInCount = regs.filter((r) => r.checked_in_at).length;

  const toggle = useMutation({
    mutationFn: async ({ memberId, next }: { memberId: string; next: boolean }) => {
      if (next) {
        const { error } = await supabase
          .from("event_registrations")
          .upsert(
            {
              event_id: event.id,
              member_id: memberId,
              status: "attended",
              checked_in_at: new Date().toISOString(),
              checked_in_by: user?.id ?? null,
            } as any,
            { onConflict: "event_id,member_id" }
          );
        if (error) throw error;
      } else {
        const { error } = await supabase
          .from("event_registrations")
          .update({ checked_in_at: null } as any)
          .eq("event_id", event.id)
          .eq("member_id", memberId);
        if (error) throw error;
      }
    },
    onMutate: async ({ memberId, next }) => {
      await qc.cancelQueries({ queryKey: key });
      const prev = qc.getQueryData<CheckinRow[]>(key) ?? [];
      const exists = prev.some((r) => r.member_id === memberId);
      const optimistic: CheckinRow[] = exists
        ? prev.map((r) =>
            r.member_id === memberId
              ? { ...r, checked_in_at: next ? new Date().toISOString() : null, status: next ? "attended" : r.status }
              : r
          )
        : [...prev, { id: `tmp-${memberId}`, member_id: memberId, checked_in_at: new Date().toISOString(), status: "attended" }];
      qc.setQueryData(key, optimistic);
      return { prev };
    },
    onError: (_e, _v, ctx) => { if (ctx?.prev) qc.setQueryData(key, ctx.prev); },
    onSettled: () => {
      qc.invalidateQueries({ queryKey: key });
      qc.invalidateQueries({ queryKey: ["event_registrations", event.id] });
      qc.invalidateQueries({ queryKey: ["event_counts"] });
    },
  });

  const list = useMemo(() => {
    const term = q.trim().toLowerCase();
    return members
      .filter((m) => {
        if (onlyCheckedIn && !regByMember.get(m.id)?.checked_in_at) return false;
        if (!term) return true;
        return (
          `${m.first_name} ${m.last_name}`.toLowerCase().includes(term) ||
          (m.member_number ?? "").toLowerCase().includes(term) ||
          (m.email ?? "").toLowerCase().includes(term)
        );
      })
      .sort((a, b) => {
        const aIn = !!regByMember.get(a.id)?.checked_in_at;
        const bIn = !!regByMember.get(b.id)?.checked_in_at;
        if (aIn !== bIn) return aIn ? -1 : 1;
        return `${a.first_name} ${a.last_name}`.localeCompare(`${b.first_name} ${b.last_name}`, "sv");
      })
      .slice(0, 300);
  }, [members, q, onlyCheckedIn, regByMember]);

  const loading = loadingMembers || loadingRegs;

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center sm:p-4 bg-black/60" onClick={onClose}>
      <div className="bg-background w-full sm:max-w-2xl h-[92vh] sm:h-auto sm:max-h-[90vh] rounded-t-2xl sm:rounded-2xl flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="px-5 py-4 border-b border-border">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <h3 className="font-display font-semibold text-lg truncate">Närvaro · {event.title}</h3>
              <div className="text-xs text-muted-foreground mt-0.5">
                {new Date(event.starts_at).toLocaleString("sv-SE", { day: "2-digit", month: "long", hour: "2-digit", minute: "2-digit" })}
              </div>
            </div>
            <button onClick={onClose} aria-label="Stäng" className="p-1.5 rounded-lg hover:bg-muted"><X size={18} /></button>
          </div>

          <div className="mt-3 flex items-center gap-3">
            <div className="inline-flex items-center gap-2 bg-emerald-500/10 text-emerald-700 px-3 py-1.5 rounded-full text-sm font-display font-semibold">
              <UserCheck size={14} /> {checkedInCount} avprickade
            </div>
            <div className="text-xs text-muted-foreground">av {regs.length} registrerade · max {event.max_attendees} platser</div>
          </div>

          <div className="mt-3 flex flex-col sm:flex-row gap-2">
            <div className="relative flex-1">
              <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Sök namn, medlemsnummer eller e-post…"
                className="w-full bg-white border border-border rounded-lg pl-9 pr-3 py-2.5 text-sm"
              />
            </div>
            <label className="flex items-center gap-2 text-sm text-muted-foreground px-1">
              <input type="checkbox" checked={onlyCheckedIn} onChange={(e) => setOnlyCheckedIn(e.target.checked)} /> Visa endast avprickade
            </label>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-3 sm:p-5">
          {loading && <p className="text-sm text-muted-foreground py-6 text-center">Laddar…</p>}
          {!loading && !list.length && <p className="text-sm text-muted-foreground py-8 text-center">Inga medlemmar matchar sökningen.</p>}
          <div className="space-y-2">
            {list.map((m) => {
              const reg = regByMember.get(m.id);
              const isIn = !!reg?.checked_in_at;
              return (
                <button
                  key={m.id}
                  type="button"
                  onClick={() => toggle.mutate({ memberId: m.id, next: !isIn })}
                  className={`w-full text-left flex items-center gap-3 p-3 rounded-xl border transition ${isIn ? "border-emerald-500/40 bg-emerald-500/10" : "border-border bg-white hover:bg-muted"}`}
                >
                  <span className={`shrink-0 w-6 h-6 rounded-md border flex items-center justify-center ${isIn ? "bg-emerald-600 border-emerald-600 text-white" : "border-border bg-white"}`}>
                    {isIn && <CheckCircle2 size={14} />}
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block font-display font-semibold text-sm truncate">{m.first_name} {m.last_name}</span>
                    <span className="block text-xs text-muted-foreground truncate">
                      <span className="font-mono">{m.member_number}</span>{m.email ? ` · ${m.email}` : ""}
                    </span>
                  </span>
                  <span className="shrink-0 text-[10px] uppercase tracking-wider font-display font-semibold text-muted-foreground">
                    {isIn ? "Närvarade" : reg ? "Anmäld" : ""}
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}

function CampaignDialog({ event, storeName, onClose, onSent }: { event: EventRow; storeName: string; onClose: () => void; onSent: (n: number) => void }) {
  const [type, setType] = useState<"invite" | "reminder">("invite");
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [touched, setTouched] = useState(false);
  const [count, setCount] = useState<number | null>(null);
  const [sending, setSending] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const preview = useQuery({
    queryKey: ["event-campaign-preview", event.id, type],
    queryFn: async () => {
      const { data, error } = await supabase.functions.invoke("send-event-campaign", {
        body: { eventId: event.id, type, dryRun: true },
      });
      if (error) throw error;
      return data as { recipients: number; subject: string; body: string };
    },
  });

  const d = preview.data;
  if (d && !touched && (subject !== d.subject || body !== d.body || count !== d.recipients)) {
    setSubject(d.subject); setBody(d.body); setCount(d.recipients);
  }

  const send = async () => {
    setSending(true); setError(null);
    try {
      const { data, error } = await supabase.functions.invoke("send-event-campaign", {
        body: { eventId: event.id, type, subject, body },
      });
      if (error) throw error;
      if ((data as any)?.error) throw new Error((data as any).error);
      onSent((data as any)?.queued ?? 0);
    } catch (e: any) {
      setError(e?.message ?? "Utskicket kunde inte köas");
      setConfirming(false);
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60">
      <div className="bg-background rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-6 py-4 border-b border-border">
          <h3 className="font-display font-semibold text-lg">Utskick — {event.title}</h3>
          <button onClick={onClose} aria-label="Stäng" className="p-1.5 rounded-lg hover:bg-muted"><X size={18} /></button>
        </div>
        <div className="p-6 space-y-4">
          <div className="flex gap-2">
            {(["invite", "reminder"] as const).map((t) => (
              <button
                key={t}
                onClick={() => { setType(t); setTouched(false); }}
                className={`px-4 py-2 rounded-full text-sm font-display transition ${type === t ? "bg-[var(--color-primary)] text-white" : "border border-border hover:bg-muted"}`}
              >
                {t === "invite" ? "Inbjudan (alla medlemmar)" : "Påminnelse (anmälda)"}
              </button>
            ))}
          </div>

          <div className="p-3 rounded-lg bg-muted text-sm">
            {preview.isLoading ? "Räknar mottagare…" : `Skickas till ${count ?? 0} mottagare · ${storeName}`}
          </div>

          <div>
            <label className="text-xs text-muted-foreground uppercase tracking-wider">Ämne</label>
            <input className="input-mini" value={subject} onChange={(e) => { setTouched(true); setSubject(e.target.value); }} />
          </div>
          <div>
            <label className="text-xs text-muted-foreground uppercase tracking-wider">Text</label>
            <textarea rows={6} className="input-mini" value={body} onChange={(e) => { setTouched(true); setBody(e.target.value); }} />
          </div>

          <div className="rounded-lg border border-border p-4 bg-white">
            <div className="text-xs text-muted-foreground uppercase tracking-wider mb-2">Förhandsvisning</div>
            <div className="font-display font-semibold text-sm mb-1">{subject}</div>
            <div className="text-sm text-muted-foreground whitespace-pre-wrap">{body}</div>
          </div>

          {error && (
            <div className="p-3 rounded-lg border border-destructive/40 bg-destructive/10 text-sm flex items-center gap-2">
              <AlertTriangle size={14} className="text-destructive" /> {error}
            </div>
          )}

          {confirming ? (
            <div className="flex gap-2">
              <button onClick={send} disabled={sending} className="flex-1 py-2.5 rounded-full bg-[var(--color-primary)] text-white text-sm font-display font-semibold disabled:opacity-50">
                {sending ? "Köar…" : `Ja, skicka till ${count ?? 0}`}
              </button>
              <button onClick={() => setConfirming(false)} className="px-4 py-2.5 rounded-full border border-border text-sm font-display">Avbryt</button>
            </div>
          ) : (
            <button
              onClick={() => setConfirming(true)}
              disabled={!subject || !body || !count}
              className="w-full inline-flex items-center justify-center gap-2 py-2.5 rounded-full bg-[var(--color-primary)] text-white text-sm font-display font-semibold disabled:opacity-40"
            >
              <Send size={14} /> Skicka utskick
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
